import type { FC } from 'react';
import { useContext, useEffect, useMemo } from 'react';
import type { AxisScale } from '@visx/axis';
import type { DataContextType, SeriesProps } from '@visx/xychart';
import { DataContext } from '@visx/xychart';

import {
  BAR_GROUPED_INTRA_GROUP_GAP_PX,
  BAR_MIN_HEIGHT_PX,
  SERIES_EVENT_SOURCE_BAR,
  SERIES_EVENT_SOURCE_XY,
} from '../../constants';

import { useSeriesEvents } from './hooks/useSeriesEvents';
import { isValidNumber } from './typeguards/isValidNumber';
import type { Bar, BarsProps } from './types';
import { getScaleBandwidth } from './utils/getScaleBandwidth';
import { getScaleBaseline } from './utils/getScaleBaseline';
import { getScaledValueFactory } from './utils/getScaledValueFactory';

/**
 * Copied from https://github.com/airbnb/visx/blob/master/packages/visx-xychart/src/components/series/private/BaseBarSeries.tsx
 * With the following changes:
 * - Added radius prop to the BarsComponent
 * - Added minimum bar height to ensure visibility
 * - Added stackOffsetAccessor to handle stacked bar charts
 * - Added groupLayout to support grouped (side-by-side) bars
 * - And some changes to Bar component & props
 */

/**
 * Grouped (dodged) bar layout: subdivide the band so multiple BarSeries siblings
 * render side-by-side instead of stacked. `seriesIndex` is this series' position
 * among siblings, `totalSeries` is the count of siblings sharing the band.
 */
export type BarGroupLayout = {
  seriesIndex: number;
  totalSeries: number;
};

export type BaseBarSeriesProps<XScale extends AxisScale, YScale extends AxisScale, Datum extends object> = SeriesProps<
  XScale,
  YScale,
  Datum
> & {
  /** Rendered component which is passed BarsProps by BaseBarSeries after processing. */
  BarsComponent: FC<BarsProps<XScale, YScale>>;
  /**
   * Specify bar padding when bar thickness does not come from a `band` scale.
   * Accepted values are [0, 1], 0 = no padding, 1 = no bar, defaults to 0.1.
   */
  barPadding?: number;
  /** Given a Datum, returns its color. Falls back to theme color if unspecified or if a null-ish value is returned. */
  colorAccessor?: (d: Datum, index: number) => string | null | undefined;
  /**
   * Accessor function that returns the stack offset for a datum.
   * Used for stacked bar charts where bars start from the sum of previous series values.
   * If not provided, bars start from zero (baseline).
   */
  stackOffsetAccessor?: (d: Datum) => number;
  /**
   * Accessor function that returns whether this series is the topmost visible bar at a given x-position.
   * Used to determine which bars should have rounded top corners.
   */
  isTopBarAtXAccessor?: (d: Datum) => boolean;
  /**
   * Whether sibling BarSeries should stack (true / undefined) or render side-by-side (false).
   * Set on the public BarSeries component; threaded through to control layout.
   * @default true
   */
  stacked?: boolean;
  /**
   * When provided, render bars in a grouped (dodged) layout: each series occupies
   * a 1/totalSeries slice of the band, offset by seriesIndex.
   */
  groupLayout?: BarGroupLayout;
  /**
   * SVG clip-path reference (e.g. `url(#chart-clip-xyz)`) applied to the series' `<g>` wrapper
   * so bars are clipped to the chart's inner plot area and never paint over axes or outside the
   * bounds. Mirrors how AreaSeries clips its series element.
   */
  clipPath?: string;
  /**
   * Opacity applied to the series' `<g>` wrapper (0–1). Fades the whole series —
   * bars and stack gap lines — as one unit, e.g. to de-emphasize it while another
   * series is spotlighted. Defaults to fully opaque.
   */
  opacity?: number;
} & Pick<
    BarsProps<XScale, YScale>,
    'radius' | 'radiusAll' | 'radiusTop' | 'radiusRight' | 'radiusBottom' | 'radiusLeft' | 'showStrokeGap'
  >;

// Fallback bandwidth estimate assumes no missing data values (divides chart space by # datum)
const getFallbackBandwidth = (fullBarWidth: number, barPadding: number) =>
  // clamp padding to [0, 1], bar thickness = (1-padding) * availableSpace
  fullBarWidth * (1 - Math.min(1, Math.max(0, barPadding)));

function BaseBarSeriesInternal<XScale extends AxisScale, YScale extends AxisScale, Datum extends object>({
  BarsComponent,
  barPadding = 0.1,
  colorAccessor,
  data,
  dataKey,
  onBlur,
  onFocus,
  onPointerMove,
  onPointerOut,
  onPointerUp,
  onPointerDown,
  enableEvents = true,
  xAccessor,
  xScale,
  yAccessor,
  yScale,
  stackOffsetAccessor,
  isTopBarAtXAccessor,
  groupLayout,
  // Applied to the `<g>` wrapper below — destructured here so they don't also leak onto each
  // bar element via `...barComponentProps`.
  clipPath,
  opacity,
  // `stacked` is consumed by the wrapper to compute groupLayout — strip it here so it doesn't
  // leak onto the DOM via `...barComponentProps`.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  stacked: _stacked,
  ...barComponentProps
}: BaseBarSeriesProps<XScale, YScale, Datum> & Pick<DataContextType<XScale, YScale, Datum>, 'xScale' | 'yScale'>) {
  const { colorScale, horizontal, theme, innerWidth = 0, innerHeight = 0 } = useContext(DataContext);
  const getScaledX = useMemo(() => getScaledValueFactory(xScale, xAccessor), [xScale, xAccessor]);
  const getScaledY = useMemo(() => getScaledValueFactory(yScale, yAccessor), [yScale, yAccessor]);
  const scaleBandwidth = getScaleBandwidth(horizontal ? yScale : xScale);
  const barThickness =
    scaleBandwidth || getFallbackBandwidth((horizontal ? innerHeight : innerWidth) / data.length, barPadding);

  // Grouped layout: bars share the band side-by-side instead of stacking.
  // Each series gets a 1/totalSeries slice; minus a 1px intra-group gap so bars don't touch.
  const subBandwidth =
    groupLayout && groupLayout.totalSeries > 0 ? barThickness / groupLayout.totalSeries : barThickness;
  const groupedBarWidth = groupLayout ? Math.max(0, subBandwidth - BAR_GROUPED_INTRA_GROUP_GAP_PX) : barThickness;
  const groupOffset = groupLayout ? groupLayout.seriesIndex * subBandwidth : 0;

  const xZeroPosition = useMemo(() => (xScale ? getScaleBaseline(xScale) : 0), [xScale]);
  const yZeroPosition = useMemo(() => (yScale ? getScaleBaseline(yScale) : 0), [yScale]);

  const color = colorScale?.(dataKey) ?? theme?.colors?.[0] ?? '#222';

  const bars: Bar[] = useMemo(() => {
    // Horizontal: keep behavior unchanged (grouped not yet supported for horizontal layout).
    // Vertical stacked (no groupLayout): bar centered on band — xOffset = -barThickness/2.
    // Vertical grouped: shift bar into its sub-band and pad by half the intra-group gap.
    const groupedXOffset = groupLayout ? groupOffset + BAR_GROUPED_INTRA_GROUP_GAP_PX / 2 : 0;
    const xOffset = horizontal ? 0 : -barThickness / 2 + groupedXOffset;
    const yOffset = horizontal ? -barThickness / 2 : 0;
    return data
      .map((datum: Datum, index: number) => {
        const x = getScaledX(datum) + xOffset;
        if (!isValidNumber(x)) {
          return null;
        }

        // Get the raw y value and stack offset
        const dataValue = yAccessor(datum);
        if (typeof dataValue !== 'number') {
          return null;
        }

        const stackOffset = stackOffsetAccessor ? stackOffsetAccessor(datum) : 0;

        // For stacked bars:
        // - Bar top is at (stackOffset + value)
        // - Bar bottom is at stackOffset
        // Scale these to pixel positions
        const barTopValue = stackOffset + dataValue;
        const barBottomValue = stackOffset;

        // Use yScale to convert values to pixel positions
        // Note: In SVG, y=0 is at the top, so larger values have smaller y positions
        const barTopY = typeof yScale === 'function' ? (yScale(barTopValue) as number) : 0;
        const barBottomY = typeof yScale === 'function' ? (yScale(barBottomValue) as number) : yZeroPosition;

        if (!isValidNumber(barTopY) || !isValidNumber(barBottomY)) {
          return null;
        }

        // Check if value is positive to apply minimum height
        const hasPositiveValue = dataValue > 0;

        // Calculate bar height (barBottomY - barTopY because SVG y-axis is inverted)
        const calculatedHeight = barBottomY - barTopY;
        if (!isValidNumber(calculatedHeight)) {
          return null;
        }

        // Calculate dimensions with minimum height for positive values
        const calculatedWidth = groupedBarWidth;
        const finalWidth = calculatedWidth;
        const finalHeight = hasPositiveValue ? Math.max(BAR_MIN_HEIGHT_PX, calculatedHeight) : calculatedHeight;

        // When min height is applied, adjust y position so bar extends upward from bottom
        const minHeightApplied = hasPositiveValue && calculatedHeight < BAR_MIN_HEIGHT_PX;
        const finalY = minHeightApplied ? barBottomY - finalHeight : barTopY;

        return {
          key: `${index}`,
          x: horizontal ? xZeroPosition + Math.min(0, calculatedHeight) : x,
          y: horizontal ? getScaledY(datum) + yOffset : finalY,
          width: horizontal ? Math.abs(calculatedHeight) : finalWidth,
          height: horizontal ? barThickness : finalHeight,
          fill: colorAccessor?.(datum, index) ?? color,
          isTopBar: isTopBarAtXAccessor ? isTopBarAtXAccessor(datum) : false,
          originalHeight: calculatedHeight,
        };
      })
      .filter((bar: Bar | null) => bar) as Bar[];
  }, [
    barThickness,
    groupedBarWidth,
    groupOffset,
    groupLayout,
    color,
    colorAccessor,
    data,
    getScaledX,
    getScaledY,
    horizontal,
    xZeroPosition,
    yZeroPosition,
    yAccessor,
    yScale,
    stackOffsetAccessor,
    isTopBarAtXAccessor,
  ]);

  const ownEventSourceKey = `${SERIES_EVENT_SOURCE_BAR}-${dataKey}`;
  const eventEmitters = useSeriesEvents<XScale, YScale, Datum>({
    dataKey,
    enableEvents,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [SERIES_EVENT_SOURCE_XY, ownEventSourceKey],
  });

  return (
    <g className="vx-bar-series" clipPath={clipPath} opacity={opacity}>
      <BarsComponent
        bars={bars}
        horizontal={horizontal}
        xScale={xScale}
        yScale={yScale}
        {...eventEmitters}
        {...barComponentProps}
      />
    </g>
  );
}

export function BaseBarSeries<XScale extends AxisScale, YScale extends AxisScale, Datum extends object>(
  props: BaseBarSeriesProps<XScale, YScale, Datum>,
) {
  const { dataKey, data, xAccessor, yAccessor, stacked = true } = props;
  const { xScale, yScale, dataRegistry } = useContext(DataContext) as Partial<DataContextType<XScale, YScale, Datum>>;

  useEffect(() => {
    if (dataRegistry) {
      dataRegistry.registerData({ key: dataKey, data, xAccessor, yAccessor });
    }
    return () => dataRegistry?.unregisterData(dataKey);
    // `stacked` is included so that toggling between stacked/grouped forces re-registration.
    // The parent reverses JSX order for stacked mode; without this dep the registry retains
    // the stale mount-order after a mode switch (React reconciles by key without unmounting).
  }, [dataRegistry, dataKey, data, xAccessor, yAccessor, stacked]);

  const registryEntry = dataRegistry?.get(dataKey);

  // Determine series position in stack for styling and offset calculation
  // Visual stacking order matches JSX order: first series on TOP, last series at BOTTOM
  // Must not memoize: registry is populated by child effects, so keys() must be read each render.
  const seriesKeys = dataRegistry?.keys() ?? [];
  const seriesIndex = seriesKeys.indexOf(dataKey);
  // Top-most series (first in JSX order, index 0) should have rounded top corners
  const isTopMostSeries = !dataRegistry || seriesIndex === 0;

  // Compute stack offset accessor - sums values from all SUBSEQUENT series at each x-position
  // Subsequent series are those with higher indices (rendered after this one, at bottom of stack)
  // This ensures JSX order matches visual order: first series on top, last series at bottom
  const stackOffsetAccessor = useMemo(() => {
    if (!stacked || !dataRegistry || seriesIndex === seriesKeys.length - 1 || seriesIndex === -1) {
      // Grouped layout, last series, or no registry - bars start from baseline.
      return undefined;
    }

    // Get all subsequent series (indices seriesIndex+1 to end)
    const subsequentSeriesKeys = seriesKeys.slice(seriesIndex + 1);

    // Build a map of x-value -> sum of subsequent series values
    // This is computed once and used for all data points
    const offsetMap = new Map<string | number, number>();

    for (const nextKey of subsequentSeriesKeys) {
      const nextEntry = dataRegistry.get(nextKey);
      if (!nextEntry) {
        continue;
      }

      for (const datum of nextEntry.data) {
        const xValue = nextEntry.xAccessor(datum);
        const yValue = nextEntry.yAccessor(datum);

        if (xValue !== null && xValue !== undefined && typeof yValue === 'number' && yValue > 0) {
          const currentOffset = offsetMap.get(xValue) ?? 0;
          offsetMap.set(xValue, currentOffset + yValue);
        }
      }
    }

    // Return accessor function that looks up the offset for each datum's x-value
    return (datum: Datum) => {
      const xValue = xAccessor(datum);
      return offsetMap.get(xValue) ?? 0;
    };
  }, [stacked, dataRegistry, seriesIndex, seriesKeys, xAccessor]);

  // Compute accessor for determining if this series is the topmost visible bar at each x-position
  // A bar is topmost if no preceding series (lower index) has data at that x-position
  const isTopBarAtXAccessor = useMemo(() => {
    if (!stacked) {
      // Grouped layout: every bar stands alone in its own column, so each is "topmost".
      return () => true;
    }
    if (!dataRegistry || seriesIndex === -1) {
      // No registry or series not found - assume all bars are topmost
      return () => true;
    }

    if (seriesIndex === 0) {
      // First series is always topmost where it has data
      return () => true;
    }

    // Get all preceding series (indices 0 to seriesIndex-1)
    const precedingSeriesKeys = seriesKeys.slice(0, seriesIndex);

    // Build a set of x-values where preceding series have positive data
    // If any preceding series has data at an x-value, this series is NOT topmost there
    const xValuesWithPrecedingData = new Set<string | number>();

    for (const prevKey of precedingSeriesKeys) {
      const prevEntry = dataRegistry.get(prevKey);
      if (!prevEntry) {
        continue;
      }

      for (const datum of prevEntry.data) {
        const xValue = prevEntry.xAccessor(datum);
        const yValue = prevEntry.yAccessor(datum);

        // A preceding series has data if yValue is a positive number
        if (xValue !== null && xValue !== undefined && typeof yValue === 'number' && yValue > 0) {
          xValuesWithPrecedingData.add(xValue);
        }
      }
    }

    // Return accessor that checks if this bar is topmost at its x-position
    return (datum: Datum) => {
      const xValue = xAccessor(datum);
      // This bar is topmost if NO preceding series has data at this x-value
      return !xValuesWithPrecedingData.has(xValue);
    };
  }, [stacked, dataRegistry, seriesIndex, seriesKeys, xAccessor]);

  // Build grouped (dodged) layout when stacked === false. seriesIndex/totalSeries
  // come from the registry order (= JSX order), giving deterministic side-by-side placement.
  const groupLayout: BarGroupLayout | undefined =
    !stacked && seriesIndex >= 0 && seriesKeys.length > 0 ? { seriesIndex, totalSeries: seriesKeys.length } : undefined;

  // if scales or data are not available in context, render nothing
  if (!xScale || !yScale || !registryEntry) {
    return null;
  }

  // otherwise pass props + over-write data/accessors
  return (
    <BaseBarSeriesInternal
      {...props}
      xScale={xScale}
      yScale={yScale}
      data={registryEntry.data}
      xAccessor={registryEntry.xAccessor}
      yAccessor={registryEntry.yAccessor}
      stackOffsetAccessor={stackOffsetAccessor}
      isTopBarAtXAccessor={isTopBarAtXAccessor}
      groupLayout={groupLayout}
      showStrokeGap={stacked && !isTopMostSeries}
    />
  );
}
